/**
 * The menus' machinery, shared by the game (`createMenus`) and the `?ui=` fixture routes
 * (`showFixture`): the backdrop and the 640x360 root, its integer scale, the one keydown
 * listener, the root classes the settings drive, and the `Ctx` every screen is handed.
 */

import './base.css';
import './controls.css';
import './access.css';
import type { Deployment, GameApi } from '../../meta/api';
import type { Ctx, SuspendedMission } from './context';
import type { MenuPorts } from './controls/bindingsPort';
import { el } from './dom';
import { createNav } from './flow';
import { Router } from './router';
import { chooseLayout } from './scale';
import type { Settings, SettingsStore } from './settings';
import { safeDescribe } from './text';

export interface MountOptions {
  root: HTMLElement;
  api: GameApi;
  settings: SettingsStore;
  /** The player committed to a mission; the menus are already hidden when this runs. */
  onFly(deployment: Deployment): void;
  suspended(): SuspendedMission | null;
  ports: MenuPorts;
}

export interface Mounted {
  readonly ctx: Ctx;
  setVisible(on: boolean): void;
  isDisposed(): boolean;
  dispose(): void;
}

/**
 * Classes on the menu root that follow the settings: the text size steps (08 §4), the
 * colour-blind accent swap (05 §4.3) and the readable long-text font. Rules are in base.css
 * and access.css.
 */
export const ROOT_CLASSES: ReadonlyArray<[className: string, on: (s: Settings) => boolean]> = [
  ['menu-text-15', (s) => s.textSize === 1.5],
  ['menu-text-2', (s) => s.textSize === 2],
  ['menu-cb', (s) => s.colourBlind],
  ['menu-readable', (s) => s.readableFont],
];

export function mountMenus(options: MountOptions): Mounted {
  const { api, settings, ports } = options;
  const backdrop = el('div', 'menu-backdrop menu-hidden');
  const canvas = el('div', 'menu-root');
  backdrop.append(canvas);
  options.root.append(backdrop);

  const router = new Router(canvas);
  let visible = false;
  let disposed = false;

  const setVisible = (on: boolean): void => {
    visible = on;
    backdrop.classList.toggle('menu-hidden', !on);
    if (on) layout();
    else router.clear();
  };

  const ctx: Ctx = {
    api,
    describe: (id) => safeDescribe((x) => api.describe(x), id),
    settings,
    nav: createNav(() => ctx, (make) => router.show(make)),
    timers: router.timers,
    openModal: (modal) => router.openModal(modal),
    closeModal: () => router.closeModal(),
    fly: (deployment) => {
      // Hidden before the hand-over, so a second Enter cannot deploy twice while the mission loads.
      setVisible(false);
      options.onFly(deployment);
    },
    suspended: () => {
      const held = options.suspended();
      if (held === null) return null;
      return {
        label: held.label,
        resume: () => {
          setVisible(false);
          held.resume();
        },
      };
    },
    ...(ports.bindings === undefined ? {} : { bindings: ports.bindings }),
    ...(ports.cloud === undefined ? {} : { cloud: ports.cloud }),
    ...(ports.wall === undefined ? {} : { wall: ports.wall }),
    ...(ports.photos === undefined ? {} : { photos: ports.photos }),
  };

  function layout(): void {
    const fit = chooseLayout(window.innerWidth, window.innerHeight, window.devicePixelRatio);
    canvas.style.left = `${fit.left}px`;
    canvas.style.top = `${fit.top}px`;
    canvas.style.transform = `scale(${fit.scale})`;
  }

  const applyClasses = (s: Settings): void => {
    for (const [className, on] of ROOT_CLASSES) canvas.classList.toggle(className, on(s));
  };

  const onKey = (ev: KeyboardEvent): void => {
    if (!visible || disposed) return;
    if (router.handleKey(ev)) ev.preventDefault();
  };
  const onResize = (): void => {
    if (visible) layout();
  };

  applyClasses(settings.get());
  const unsubscribe = settings.subscribe(applyClasses);
  window.addEventListener('keydown', onKey);
  window.addEventListener('resize', onResize);
  // A right-click must not open the browser menu over the terminal.
  backdrop.addEventListener('contextmenu', (ev) => ev.preventDefault());

  return {
    ctx,
    setVisible,
    isDisposed: () => disposed,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      setVisible(false);
      unsubscribe();
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      backdrop.remove();
    },
  };
}
